import React, { useEffect, useRef } from "react";
import {
  paginationItem,
  paginationTotal,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHeader,
  StyledTableRow,
} from "../../../common/components/table";
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableCell,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  Person2Outlined,
  Email,
  MarkEmailRead,
  MoreVert,
} from "@mui/icons-material";
import { paginationCont } from "../../products/sxprops";
import { UseQueryResult } from "@tanstack/react-query";
import { emptyCont, imageCell } from "../sxprops";
import { PagedUserManagementModel } from "../api/types";
import useMainStore from "../../landing-page/api/store";
import useUserManagementStore from "../api/store";
import { Handlers } from "../api/handlers";
import { useLockUser } from "../api/hooks";
import { pascalToRegular } from "../../../common/enums/user-roles";
import { getHighestRole } from "../../../common/utils/role-identifier";
import {
  getBizboxUserPrivileges,
  getGroupAdminPrivileges,
} from "../../../common/auth/privileges";
import { useAuthStore } from "../../../common/auth/store";
import useAccessibilityModalStore from "../manage-access/store";
import { useLocation } from "react-router-dom";
import { ActionMenu } from "./action-menu";
import { EmailResendModule } from "../../../common/enums/email-module";
import { getBizboxUserDepartmentLabel } from "../../../common/enums/bizbox-user-department";
import { ResendEmailModel, useResendEmail } from "../../users/api/hooks";

interface IDataTable {
  getPagedBizboxUsers: UseQueryResult<PagedUserManagementModel, Error>;
  isInternal: boolean;
  walkthroughMenuOpen?: boolean;
}

type RowType = PagedUserManagementModel["items"][number];

export const DataTable: React.FC<IDataTable> = ({
  getPagedBizboxUsers,
  isInternal,
  walkthroughMenuOpen = false,
}) => {
  const authStore = useAuthStore();
  const groupAdminPrivileges = getGroupAdminPrivileges(authStore.privileges);
  const bizboxUserPrivileges = getBizboxUserPrivileges(authStore.privileges);

  const theme = useTheme();
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mainStore = useMainStore();

  const { mutate: lockUser, status } = useLockUser(isInternal);

  const resendModule = isInternal
    ? EmailResendModule.BizBoxUsers
    : EmailResendModule.GroupAdmins;

  const resendEmail = useResendEmail(resendModule);

  const isMobile = useMediaQuery(theme.breakpoints.down("laptop"));
  const { setUserData } = useAccessibilityModalStore();

  const {
    initialUserManagementState,
    setUserManagementPage,
    setSelectedUserId,
    setActiveUser,
  } = useUserManagementStore();

  const { isFetching, data } = getPagedBizboxUsers;

  const totalPages = Math.ceil(
    (data?.totalItems || 0) / initialUserManagementState.pageSize,
  );

  const focusPage = () => {
    itemRefs.current[initialUserManagementState.pageNumber - 1]?.scrollIntoView(
      {
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      },
    );
  };

  const location = useLocation();
  const module = location.pathname;

  useEffect(() => {
    setUserManagementPage(1);
  }, [module, setUserManagementPage]);

  const handlers = new Handlers();

  const CGAccess =
    "Co Group Admin" ===
    (mainStore.currentUser?.roles &&
      pascalToRegular(getHighestRole(mainStore.currentUser?.roles)));

  const CSAccess =
    "Bizbox Admin" ===
    (mainStore.currentUser?.roles &&
      pascalToRegular(getHighestRole(mainStore.currentUser?.roles)));

  const userRole = getHighestRole(mainStore.currentUser?.roles);

  const hasBizboxAccess =
    mainStore.currentUser?.roles?.includes("SuperAdmin") ||
    mainStore.currentUser?.roles?.includes("BizboxAdmin");

  const [menuState, setMenuState] = React.useState<{
    anchorEl: HTMLElement | null;
    row: RowType | null;
  }>({
    anchorEl: null,
    row: null,
  });

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: RowType,
  ) => {
    setMenuState({ anchorEl: event.currentTarget, row });
  };

  const handleMenuClose = () => {
    setMenuState({ anchorEl: null, row: null });
  };

  useEffect(() => {
    if (walkthroughMenuOpen && data?.items?.length) {
      const btn = document.getElementById(`user-actions-${data.items[0].applicationUserId}`) as HTMLElement | null;
      if (btn) setMenuState({ anchorEl: btn, row: data.items[0] });
    } else {
      setMenuState({ anchorEl: null, row: null });
    }
  }, [walkthroughMenuOpen, data?.items]);

  const isGroupAdminAndArchived =
    mainStore.currentUser?.roles?.includes("GroupAdmin") &&
    mainStore.currentUser?.clients?.every((x) => x.isDeprecated);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <StyledTableContainer sx={{ flex: 1, minHeight: 0 }}>
        <Table sx={{ width: "100%" }}>
          <StyledTableHeader>
            <TableRow>
              {isMobile ? (
                <StyledTableCell width="5%" align="center" colSpan={3}>
                  User Details
                </StyledTableCell>
              ) : (
                <>
                  <StyledTableCell width="20%" align="center">
                    Name
                  </StyledTableCell>
                  <StyledTableCell width="20%" align="center">
                    Phone
                  </StyledTableCell>
                  <StyledTableCell width="20%" align="center">
                    Email
                  </StyledTableCell>
                  <StyledTableCell width="15%" align="center">
                    Last Login
                  </StyledTableCell>
                  {isInternal && (
                    <StyledTableCell width="15%" align="center">
                      Department
                    </StyledTableCell>
                  )}
                  <StyledTableCell width="10%" align="center">
                    Action
                  </StyledTableCell>
                </>
              )}
            </TableRow>
          </StyledTableHeader>

          <StyledTableBody>
            {isFetching && (
              <StyledTableRow>
                <StyledTableCell
                  sx={{ backgroundColor: "white", height: "height" }}
                  align="center"
                  colSpan={6}
                  rowSpan={5}
                >
                  <Box sx={emptyCont}>
                    <CircularProgress />
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            )}

            {!isFetching && data?.totalItems === 0 && (
              <StyledTableRow>
                <TableCell
                  data-testid="user-mgmt-empty-state"
                  sx={{ backgroundColor: "white", height: "100px" }}
                  align="center"
                  colSpan={6}
                  rowSpan={5}
                >
                  <Box sx={emptyCont}>
                    <Box
                      sx={{
                        textAlign: "center",
                        color: "#6D87AA",
                        fontSize: "18px",
                        fontWeight: 500,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <img
                        src="/empty-table-data-logo.svg"
                        alt="No Data"
                        style={{ maxHeight: 300 }}
                      />
                      <Box
                        component="span"
                        sx={{
                          fontFamily: "Poppins",
                          fontWeight: 600,
                          fontSize: "24px",
                          color: "#A1A1A1",
                          textAlign: "center",
                        }}
                      >
                        {initialUserManagementState.searchText
                          ? `No results found. Try adjusting your search or using different keywords.`
                          : !mainStore.selectedClient?.id
                          ? "Select a client to view users"
                          : "No Users Added"}
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
              </StyledTableRow>
            )}

            {!isFetching &&
              data?.items.map((row, i) => (
                <StyledTableRow
                  key={row.id}
                  data-testid={`user-mgmt-row-${row.applicationUserId}`}
                  sx={{ "&:hover": { backgroundColor: "#FF9484" } }}
                >
                  {isMobile ? (
                    <>
                      <StyledTableCell align="left">
                        <Box sx={{ color: "primary.main" }}>
                          <Box position="relative" width="100%" py={1}>
                            <Box display="flex" justifyContent="center">
                              <Badge
                                overlap="circular"
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right",
                                }}
                                badgeContent={
                                  (row.role === "GroupAdmin" ||
                                    row.role === "BizboxAdmin") && (
                                    <Person2Outlined
                                      sx={{ color: "#FF8C00", fontSize: 25 }}
                                    />
                                  )
                                }
                              >
                                <Box sx={imageCell}>
                                  <img
                                    src={row.photoUrl || "/bizlogo.svg"}
                                    alt="Bizbox logo"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                </Box>
                              </Badge>
                            </Box>
                            <Box
                              position="absolute"
                              top="50%"
                              right={0}
                              sx={{ transform: "translateY(-50%)" }}
                            >
                              <IconButton
                                id={`user-actions-${row.applicationUserId}`}
                                data-testid={`user-actions-${row.applicationUserId}`}
                                aria-label="user-actions"
                                onClick={(e) => handleMenuOpen(e, row)}
                                disabled={mainStore.selectedClient?.id === null}
                                sx={{ color: "secondary.main" }}
                              >
                                <MoreVert />
                              </IconButton>
                            </Box>
                          </Box>

                          <Typography variant="subtitle2" mt={1}>
                            <b>Name:</b> {row.name}
                          </Typography>
                          <Typography variant="body2" mt={1}>
                            <b>Phone:</b> {row.phone ?? "N/A"}
                          </Typography>
                          <Typography variant="body2" mt={1}>
                            <b>Email:</b> {row.email || "N/A"}
                          </Typography>
                          {isInternal && (
                            <Typography variant="body2" mt={1}>
                              <b>Department:</b>{" "}
                              {getBizboxUserDepartmentLabel(row.department)}
                            </Typography>
                          )}
                          <Typography variant="body2" mt={1}>
                            <b>Last Login:</b> ...
                          </Typography>
                        </Box>
                      </StyledTableCell>
                    </>
                  ) : (
                    <>
                      <StyledTableCell align="center">
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="left"
                        >
                          <Badge
                            overlap="circular"
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            badgeContent={
                              row.role === "GroupAdmin" ||
                              row.role === "BizboxAdmin" ? (
                                <Person2Outlined
                                  sx={{ color: "#FF8C00", fontSize: "25px" }}
                                />
                              ) : null
                            }
                          >
                            <Box sx={imageCell}>
                              <img
                                src={row.photoUrl || "/bizlogo.svg"}
                                alt="Bizbox logo"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </Box>
                          </Badge>
                          <Typography component="span" sx={{ marginLeft: 3 }}>
                            {row.name}
                          </Typography>
                        </Box>
                      </StyledTableCell>

                      <StyledTableCell
                        width="30%"
                        align="center"
                        title={row.phone?.trim() || "N/A"}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.phone?.trim() || "N/A"}
                      </StyledTableCell>

                      <StyledTableCell
                        width="30%"
                        align="center"
                        title={row.email}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.lastLoginAt ? (
                          <MarkEmailRead
                            sx={{
                              verticalAlign: "middle",
                              color: "success.main",
                              fontSize: "25px",
                            }}
                          />
                        ) : (
                          <Email
                            sx={{
                              verticalAlign: "middle",
                              color: "error.main",
                              fontSize: "25px",
                            }}
                          />
                        )}
                        <Typography
                          component="span"
                          sx={{ verticalAlign: "middle", ml: 1 }}
                        >
                          {row.email}
                        </Typography>
                      </StyledTableCell>

                      <StyledTableCell
                        align="center"
                        sx={{ color: "primary.main" }}
                      >
                        {row.lastLoginAt
                          ? `${new Date(row.lastLoginAt).toLocaleDateString(
                              "en-US",
                            )} ${new Date(row.lastLoginAt).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "numeric",
                                minute: "numeric",
                                hour12: true,
                              },
                            )}`
                          : "N/A"}
                      </StyledTableCell>

                      {isInternal && (
                        <StyledTableCell
                          align="center"
                          sx={{ color: "primary.main" }}
                        >
                          {getBizboxUserDepartmentLabel(row.department)}
                        </StyledTableCell>
                      )}

                      {/* ✅ Action column: Menu trigger only */}
                      <StyledTableCell
                        width="10%"
                        align="center"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <IconButton
                          id={`user-actions-${row.applicationUserId}`}
                          data-testid={`user-actions-${row.applicationUserId}`}
                          onClick={(e) => handleMenuOpen(e, row)}
                          disabled={
                            !walkthroughMenuOpen &&
                            (mainStore.selectedClient?.id === null ||
                              isGroupAdminAndArchived)
                          }
                          sx={{ color: "secondary.main" }}
                        >
                          <MoreVert />
                        </IconButton>
                      </StyledTableCell>
                    </>
                  )}
                </StyledTableRow>
              ))}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>

      {/* ✅ ONE menu instance for the whole table */}
      {menuState.row && (
        <ActionMenu
          anchorEl={menuState.anchorEl}
          open={Boolean(menuState.anchorEl)}
          onClose={handleMenuClose}
          row={{
            applicationUserId: menuState.row.applicationUserId,
            role: menuState.row.role,
            isAdminLocked: menuState.row.isAdminLocked,
            adminLockingReason: menuState.row.adminLockingReason,
            lastLoginAt: menuState.row.lastLoginAt,
            name: menuState.row.name,
            photoUrl: menuState.row.photoUrl,
          }}
          status={status}
          CGAccess={CGAccess}
          CSAccess={CSAccess}
          hasBizboxAccess={hasBizboxAccess}
          userRole={userRole}
          mainStoreHasClient={!!mainStore.selectedClient?.id}
          walkthroughMenuOpen={walkthroughMenuOpen}
          groupAdminPrivileges={groupAdminPrivileges}
          bizboxUserPrivileges={bizboxUserPrivileges}
          onEdit={() =>
            handlers.handleSetEditMode(menuState.row!.applicationUserId)
          }
          onResendEmail={() => {
            setActiveUser(menuState.row!);
            const data: ResendEmailModel = {
              userId: menuState.row!.applicationUserId,
            };

            resendEmail.mutate(data, {
              onSuccess: () => {
                handlers.handleOpenResetPassModal();
              },
            });
          }}
          onLock={() => {
            setSelectedUserId(menuState.row!.applicationUserId);
            handlers.handleOpenLockUserModal();
          }}
          onUnlock={() => {
            setSelectedUserId(menuState.row!.applicationUserId);
            lockUser(
              {
                userId: menuState.row!.applicationUserId,
                adminLockingReason: "",
              },
              { onSuccess: () => handlers.handleCloseLockUserModal() },
            );
          }}
          isInternal={isInternal}
          onManageAccess={() => {
            handlers.handleOpenAccessManagementModal();
            setUserData({
              userId: menuState.row!.applicationUserId,
              name: menuState.row!.name,
              photoUrl: menuState.row!.photoUrl,
            });
          }}
          onManageProducts={() => {
            setActiveUser(menuState.row!);
            handleMenuClose();
            useUserManagementStore.getState().setAssignProductsModal(true);
          }}
          onDelete={() => {
            setActiveUser(menuState.row!);
            handleMenuClose();
            handlers.handleOpenDeleteUserModal();
          }}
        />
      )}

      <Box sx={paginationCont}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <IconButton
            id="user-mgmt-pagination-prev"
            data-testid="user-mgmt-pagination-prev"
            sx={{
              color: "primary.main",
              opacity:
                initialUserManagementState.pageNumber > 1 &&
                (data?.totalItems || 0) > 0
                  ? 1
                  : 0,
            }}
            onClick={() => {
              if (initialUserManagementState.pageNumber > 1) {
                setUserManagementPage(
                  initialUserManagementState.pageNumber - 1,
                );
                focusPage();
              }
            }}
          >
            <KeyboardDoubleArrowLeft />
          </IconButton>

          {Array.from({ length: totalPages }).map((_, i) => {
            const value = i + 1;
            const isSelected: boolean =
              initialUserManagementState.pageNumber === value;
            return (
              <Box
                ref={(el: HTMLDivElement | null) => (itemRefs.current[i] = el)}
                onClick={() => {
                  setUserManagementPage(value);
                }}
                key={i}
                id={`user-mgmt-pagination-page-${value}`}
                data-testid={`user-mgmt-pagination-page-${value}`}
                sx={paginationItem(isSelected, theme)}
              >
                {value}
              </Box>
            );
          })}
        </Box>
        <IconButton
          id="user-mgmt-pagination-next"
          data-testid="user-mgmt-pagination-next"
          sx={{
            color: "primary.main",
            opacity: initialUserManagementState.pageNumber < totalPages ? 1 : 0,
          }}
          onClick={() => {
            if (initialUserManagementState.pageNumber < totalPages) {
              setUserManagementPage(initialUserManagementState.pageNumber + 1);
              focusPage();
            }
          }}
        >
          <KeyboardDoubleArrowRight />
        </IconButton>

        <Box sx={paginationTotal}>Total Users: {data?.totalItems || 0}</Box>
      </Box>
    </Box>
  );
};
